import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
  approveBudgetCenter,
  discardBudgetCenterDraft,
  fetchBudgetCenterPortfolio,
  markBudgetCenterLive,
  saveBudgetCenterDraft,
} from '@src/api/actions/dashboard/clientBudgets';

function normalizeSegment(segment) {
  return {
    budget_id: segment?.budget_id ?? segment?.id ?? null,
    source_budget_id: segment?.source_budget_id ?? segment?.id ?? null,
    vertical: segment?.vertical ?? segment?.vertical_data?.id ?? '',
    location: segment?.location ?? segment?.location_data?.id ?? '',
    channel: segment?.channel ?? segment?.channel_data?.id ?? '',
    amount: segment?.amount ?? '',
    notes: segment?.notes ?? '',
    adjustment_total: segment?.adjustment_total ?? '0.00',
    effective_amount: segment?.effective_amount ?? segment?.amount ?? '',
    location_ids: segment?.location_ids ?? (segment?.location ? [Number(segment.location)] : []),
    month_to_date_spend: segment?.month_to_date_spend ?? 0,
    projected_spend: segment?.projected_spend ?? 0,
    remaining_per_day: segment?.remaining_per_day ?? 0,
    pacing_percentage: segment?.pacing_percentage ?? null,
    workflow_status: segment?.workflow_status ?? 'draft',
    has_pending_turn_off: Boolean(segment?.has_pending_turn_off),
    is_turned_off: Boolean(segment?.is_turned_off),
    adjustments: Array.isArray(segment?.adjustments) ? segment.adjustments : [],
    vertical_data: segment?.vertical_data ?? null,
    location_data: segment?.location_data ?? null,
    channel_data: segment?.channel_data ?? null,
    pendingAdjustment: {
      amount_delta: '',
      effective_date: dayjs().format('YYYY-MM-DD'),
      notes: '',
    },
  };
}

function emptySegment() {
  return normalizeSegment({});
}

// Local-edit detection for reloads: compares the fields a user can edit in the
// portfolio table against what the server payload last delivered, so a
// background refresh never silently reverts in-progress edits.
function hasLocalEdits(data) {
  if (!data?.pageData) return false;
  const loaded = Array.isArray(data.pageData.segments) && data.pageData.segments.length
    ? data.pageData.segments.map((s) => normalizeSegment(s))
    : [emptySegment()];
  const current = Array.isArray(data.segments) ? data.segments : [];
  if (loaded.length !== current.length) return true;
  const comparable = (seg) => [
    seg.vertical || '',
    seg.location || '',
    [...(seg.location_ids || [])].map(Number).sort((a, b) => a - b).join(','),
    String(seg.amount ?? ''),
    seg.notes || '',
  ].join('|');
  return current.some((seg, i) => comparable(seg) !== comparable(loaded[i]));
}

/**
 * Hook for managing budget center data for multiple clients in parallel.
 * Used by parent-scope members to see all clients in one view.
 */
export default function useMultiClientBudgetCenter({ clientOptions, period, enabled, parentCompanyId }) {
  // Map of clientId -> { pageData, segments, rowErrors, saving, verticalOptions, locationOptions }
  const [clientDataMap, setClientDataMap] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const loadIdRef = useRef(0);
  // Keep the latest map in a ref so loadAll can preserve dirty clients without
  // depending on clientDataMap (which would re-create loadAll on every edit).
  const clientDataMapRef = useRef(clientDataMap);
  clientDataMapRef.current = clientDataMap;
  // In parent-company mode the backend resolves child clients itself, so the
  // key must NOT track clientOptions — those change identity on every client
  // search keystroke, and a key change re-runs loadAll, wiping unsaved edits.
  // Sort the explicit-list key so option reordering doesn't refetch either.
  const clientIdsKey = useMemo(
    () => (parentCompanyId
      ? ''
      : clientOptions.map((opt) => String(opt.value)).sort().join(',')),
    [clientOptions, parentCompanyId]
  );

  // One batched request for the whole portfolio. When a parent company is in
  // scope the backend resolves its child clients itself; otherwise we pass
  // the explicit client list. Previously this fanned out 3 requests per
  // client, which made the parent view crawl.
  const loadAll = useCallback(async () => {
    if (!enabled || !period) return;
    if (!parentCompanyId && !clientIdsKey) return;

    const loadId = ++loadIdRef.current;
    setLoading(true);
    setError(null);

    try {
      const params = { period };
      if (parentCompanyId) {
        params.parent_company_id = parentCompanyId;
      } else {
        params.client_ids = clientIdsKey;
      }
      const response = await fetchBudgetCenterPortfolio(params);
      if (loadIdRef.current !== loadId) return;

      const nextMap = {};
      (response.data?.clients || []).forEach((entry) => {
        const clientKey = String(entry.client_id);
        const pageData = entry.payload || null;
        const prevData = clientDataMapRef.current[clientKey];
        // A refresh must never revert edits the user hasn't saved yet — keep
        // the locally-edited segments and only refresh the server payload.
        const keepLocalSegments = prevData && hasLocalEdits(prevData);
        nextMap[clientKey] = {
          clientName: entry.client_name,
          pageData,
          loadError: entry.error || null,
          segments: keepLocalSegments
            ? prevData.segments
            : (pageData && Array.isArray(pageData.segments) && pageData.segments.length
              ? pageData.segments.map((s) => normalizeSegment(s))
              : [emptySegment()]),
          rowErrors: [],
          saving: false,
          verticalOptions: (entry.verticals || []).map((v) => ({ value: v.id, label: v.vertical })),
          locationOptions: (entry.territories || []).map((t) => ({
            value: t.id,
            label: t.name,
            // Verticals this market runs — scopes the segment Location picker.
            verticalIds: (t.vertical_ids || []).map(Number),
          })),
        };
      });

      setClientDataMap(nextMap);
    } catch (err) {
      if (loadIdRef.current === loadId) {
        setError('Failed to load budget data.');
      }
    } finally {
      if (loadIdRef.current === loadId) {
        setLoading(false);
      }
    }
  }, [clientIdsKey, enabled, parentCompanyId, period]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Update a segment field for a specific client
  const updateSegmentForClient = useCallback((clientId, index, field, value) => {
    setClientDataMap((prev) => {
      const data = prev[clientId];
      if (!data) return prev;
      const nextSegments = data.segments.map((seg, i) =>
        i === index ? { ...seg, [field]: value } : seg
      );
      return { ...prev, [clientId]: { ...data, segments: nextSegments } };
    });
  }, []);

  // Add segment for a specific client
  const addSegmentForClient = useCallback((clientId) => {
    setClientDataMap((prev) => {
      const data = prev[clientId];
      if (!data) return prev;
      return {
        ...prev,
        [clientId]: { ...data, segments: [...data.segments, emptySegment()] },
      };
    });
  }, []);

  // Delete segment for a specific client
  const deleteSegmentForClient = useCallback((clientId, index) => {
    setClientDataMap((prev) => {
      const data = prev[clientId];
      if (!data || data.segments.length <= 1) return prev;
      return {
        ...prev,
        [clientId]: {
          ...data,
          segments: data.segments.filter((_, i) => i !== index),
        },
      };
    });
  }, []);

  // Duplicate segment for a specific client
  const duplicateSegmentForClient = useCallback((clientId, index) => {
    setClientDataMap((prev) => {
      const data = prev[clientId];
      if (!data) return prev;
      const source = data.segments[index];
      if (!source) return prev;
      const copy = {
        ...source,
        budget_id: null,
        source_budget_id: null,
        workflow_status: 'draft',
        adjustment_total: '0.00',
        effective_amount: source?.amount ?? '',
        month_to_date_spend: '0.00',
        projected_spend: '0.00',
        remaining_per_day: '0.00',
        adjustments: [],
        pendingAdjustment: {
          amount_delta: '',
          effective_date: dayjs().format('YYYY-MM-DD'),
          notes: '',
        },
      };
      const nextSegments = [...data.segments.slice(0, index + 1), copy, ...data.segments.slice(index + 1)];
      return { ...prev, [clientId]: { ...data, segments: nextSegments } };
    });
  }, []);

  // Update pending adjustment field for a specific client segment
  const updatePendingAdjustmentForClient = useCallback((clientId, index, field, value) => {
    setClientDataMap((prev) => {
      const data = prev[clientId];
      if (!data) return prev;
      const nextSegments = data.segments.map((seg, i) =>
        i === index
          ? { ...seg, pendingAdjustment: { ...(seg.pendingAdjustment || {}), [field]: value } }
          : seg
      );
      return { ...prev, [clientId]: { ...data, segments: nextSegments } };
    });
  }, []);

  // Build payload for a specific client
  const buildPayloadForClient = useCallback((clientId) => {
    const data = clientDataMap[clientId];
    if (!data) return null;

    const pageData = data.pageData;
    const isCurrentMonth = dayjs(period).isSame(dayjs(), 'month');
    const hasLive = Array.isArray(pageData?.live_budget_ids) && pageData.live_budget_ids.length > 0;
    const isLiveMonth = isCurrentMonth && hasLive;

    const isLiveOrigin = (seg) =>
      isLiveMonth && Array.isArray(pageData?.live_budget_ids)
      && pageData.live_budget_ids.includes(Number(seg.source_budget_id || seg.budget_id));

    const segmentsToSubmit = isLiveMonth
      ? data.segments.filter((seg) => {
          if (isLiveOrigin(seg)) return (seg.adjustments || []).length > 0;
          return true;
        })
      : data.segments;

    return {
      client_id: Number(clientId),
      period,
      allocation_mode: 'optimize_freely',
      allocation_config: {},
      notes: '',
      segments: segmentsToSubmit.map((seg) => ({
        budget_id: seg.budget_id || null,
        source_budget_id: seg.source_budget_id || null,
        vertical: seg.vertical || null,
        vertical_ids: seg.vertical ? [Number(seg.vertical)] : [],
        location: seg.location || null,
        location_ids: (seg.location_ids || []).length
          ? seg.location_ids.map(Number)
          : (seg.location ? [Number(seg.location)] : []),
        channel: seg.channel || null,
        amount: seg.amount,
        notes: seg.notes || '',
      })),
    };
  }, [clientDataMap, period]);

  // Run an action for a specific client
  const runActionForClient = useCallback(async (clientId, actionFn) => {
    const data = clientDataMap[clientId];
    if (!data) return null;

    // Client-side validation
    const errors = [];
    data.segments.forEach((seg, i) => {
      const amt = seg.amount;
      if (amt === '' || amt === null || amt === undefined) {
        errors.push(`Segment ${i + 1}: Starting budget is required.`);
      } else if (Number.isNaN(Number(amt)) || Number(amt) < 0) {
        errors.push(`Segment ${i + 1}: Invalid amount.`);
      }
    });
    if (errors.length) {
      setClientDataMap((prev) => ({
        ...prev,
        [clientId]: { ...prev[clientId], error: errors.join(' ') },
      }));
      return null;
    }

    const payload = buildPayloadForClient(clientId);
    if (!payload) return null;

    setClientDataMap((prev) => ({
      ...prev,
      [clientId]: { ...prev[clientId], saving: true, error: null },
    }));

    try {
      const response = await actionFn(payload);
      const resData = response.data;
      setClientDataMap((prev) => ({
        ...prev,
        [clientId]: {
          ...prev[clientId],
          pageData: resData,
          segments: Array.isArray(resData?.segments) && resData.segments.length
            ? resData.segments.map((s) => normalizeSegment(s))
            : [emptySegment()],
          rowErrors: [],
          saving: false,
          error: null,
        },
      }));
      return resData;
    } catch (err) {
      const detail = err?.response?.data?.detail
        || err?.response?.data?.segments
        || err?.message
        || 'Failed to save.';
      setClientDataMap((prev) => ({
        ...prev,
        [clientId]: { ...prev[clientId], saving: false, error: String(detail) },
      }));
      throw err;
    }
  }, [buildPayloadForClient, clientDataMap]);

  const saveDraftForClient = useCallback(
    (clientId) => runActionForClient(clientId, saveBudgetCenterDraft),
    [runActionForClient]
  );

  const approveForClient = useCallback(
    (clientId) => runActionForClient(clientId, approveBudgetCenter),
    [runActionForClient]
  );

  const markLiveForClient = useCallback(async (clientId) => {
    const data = clientDataMap[clientId];
    if (!data) return null;

    setClientDataMap((prev) => ({
      ...prev,
      [clientId]: { ...prev[clientId], saving: true },
    }));

    try {
      const response = await markBudgetCenterLive({
        client_id: Number(clientId),
        period,
      });
      const resData = response.data;
      setClientDataMap((prev) => ({
        ...prev,
        [clientId]: {
          ...prev[clientId],
          pageData: resData,
          segments: Array.isArray(resData?.segments) && resData.segments.length
            ? resData.segments.map((s) => normalizeSegment(s))
            : [emptySegment()],
          saving: false,
        },
      }));
      return resData;
    } catch (err) {
      setClientDataMap((prev) => ({
        ...prev,
        [clientId]: { ...prev[clientId], saving: false },
      }));
      throw err;
    }
  }, [clientDataMap, period]);

  const discardForClient = useCallback(async (clientId) => {
    setClientDataMap((prev) => ({
      ...prev,
      [clientId]: { ...prev[clientId], saving: true },
    }));

    try {
      const response = await discardBudgetCenterDraft({
        client_id: Number(clientId),
        period,
      });
      const resData = response.data;
      setClientDataMap((prev) => ({
        ...prev,
        [clientId]: {
          ...prev[clientId],
          pageData: resData,
          segments: Array.isArray(resData?.segments) && resData.segments.length
            ? resData.segments.map((s) => normalizeSegment(s))
            : [emptySegment()],
          saving: false,
        },
      }));
      return resData;
    } catch (err) {
      setClientDataMap((prev) => ({
        ...prev,
        [clientId]: { ...prev[clientId], saving: false },
      }));
      throw err;
    }
  }, [period]);

  // Aggregate summaries
  const aggregates = useMemo(() => {
    const entries = Object.values(clientDataMap);
    const totalBudget = entries.reduce((sum, d) => {
      const amt = Number(d.pageData?.current_budget?.amount || 0);
      return sum + amt;
    }, 0);
    const totalSegments = entries.reduce((sum, d) => sum + (d.segments?.length || 0), 0);
    const statusCounts = {};
    entries.forEach((d) => {
      const s = String(d.pageData?.status || 'draft').toLowerCase();
      statusCounts[s] = (statusCounts[s] || 0) + 1;
    });
    return { totalBudget, totalSegments, clientCount: entries.length, statusCounts };
  }, [clientDataMap]);

  // Sorted client entries for consistent rendering
  const clientEntries = useMemo(
    () => Object.entries(clientDataMap).sort(([, a], [, b]) =>
      (a.clientName || '').localeCompare(b.clientName || '')
    ),
    [clientDataMap]
  );

  return {
    clientDataMap,
    clientEntries,
    loading,
    error,
    aggregates,
    reload: loadAll,
    updateSegmentForClient,
    addSegmentForClient,
    deleteSegmentForClient,
    duplicateSegmentForClient,
    updatePendingAdjustmentForClient,
    saveDraftForClient,
    approveForClient,
    markLiveForClient,
    discardForClient,
    clearErrorForClient: (clientId) => {
      setClientDataMap((prev) => ({
        ...prev,
        [clientId]: { ...prev[clientId], error: null },
      }));
    },
  };
}
